import getOr from 'lodash/get';
import isEmpty from 'lodash/fp/isEmpty';

import {
  Assistance,
  assistanceList,
  EmploymentData,
  IncomeData,
  FormDataType,
  DependentData,
} from 'contexts/FormProvider';
import { toExpenseArray, toNumber } from './utils';

type YesNoString = 'Yes' | 'yes' | 'No' | 'no';
type TrueFalseString = 'True' | 'False';

const toBooleanString = (value: YesNoString): TrueFalseString => {
  const lowercaseValue = value.toLowerCase();

  if (lowercaseValue === 'yes') {
    return 'True';
  }
  return 'False';
};

const assistanceListValues = [
  'ExtensionOfDueDateForPayment',
  'PayingAReducedLumpSum',
  'PayingInInstalments',
  'PostponingOneOrMoreInstalments',
  'Other',
];

export const remapFHA = (data: Partial<FormDataType>) => {
  const get = (key: keyof FormDataType, defaultValue: string | null = null) => {
    if (isEmpty(data)) {
      return null;
    }
    return getOr(data, key, defaultValue);
  };
  const getAssistanceIndex = assistanceList.findIndex((a: Assistance) => get('assistance') === a);

  const haveRep = get('nominatedRepType') === 'yes';
  const haveRepWithPhone = haveRep && get('repContactMethod') === 'phone';

  const dependents = [...getOr(data, 'dependents', [])].map(({ dependentName, dependentAge }: DependentData) => ({
    dependent_name: dependentName,
    dependent_age: dependentAge,
  }));

  const isEmployed = get('employmentStatus') === 'yes';

  const employmentDataArray = [...getOr(data, 'employmentData', [])].map((cur: EmploymentData) => ({
    employment_type: cur.employmentType,
    employer_name: cur.employerName,
    occupation: cur.occupation,
    contact_name: cur.contactPerson,
    contact_number: cur.contactPersonNumber,
    salary_per_month: toNumber(cur.salary),
  }));

  const employmentData = isEmployed ? employmentDataArray : null;

  const haveIncome = get('incomeStatus') === 'yes';

  const incomeDataArray = [...getOr(data, 'incomeData', [])].map((cur: IncomeData) => ({
    income_type: cur.incomeType,
    income_description: cur.incomeDescription,
    income_amount_per_month: toNumber(cur.incomeAmount),
  }));

  const incomes = haveIncome ? incomeDataArray : null;

  const isDeclared = 'TRUE';

  const expenses = {
    rent_mortgage: get('rentMortgage'),
    child_support: get('childSupport'),
    other_loan_payments: get('otherLoan'),
    motor_vehicle_expenses: get('motorVehicle'),
    credit_card: get('creditCard'),
    living_costs: get('livingCosts'),
    utilities: get('utilities'),
    school_fees: get('schoolFees'),
    hospital_medical: get('hospitalMedicalCosts'),
    insurance: get('insurance'),
    other: get('otherExpense'),
  };

  // WIP

  return {
    your_details: {
      contact_reason: {
        policy_or_claim: 'claim',
        claim_number: get('claimNumber'),
      },

      personal_details: {
        title: get('title'),
        first_name: get('firstname'),
        last_name: get('lastname'),
        dependent: dependents,
      },

      contact_details: {
        language_interpreter_required: toBooleanString(get('isLangInterpreter') as YesNoString),
        language: get('isLangInterpreter') === 'yes' ? get('language') : null,
        other_language: get('isLangInterpreter') === 'yes' && get('language') === 'Other' ? get('otherLanguage') : null,
        preferred_method_of_contact: get('iContactPreferedMethod'),
        email_address: get('cEmailAddress'),
        physical_address: {
          country: get('phyCountry'),
          street_address: get('phyStreetAddress'),
          suburb: get('phySuburb'),
          state: get('phyState'),
          postcode: get('phyPostcode'),
        },
        postal_address_is_same_as_physical_address: get('addressType'),
        postal_address:
          get('addressType') === 'no'
            ? {
                country: get('posCountry'),
                street_address: get('posStreetAddress'),
                suburb: get('posSuburb'),
                state: get('posState'),
                postcode: get('posPostcode'),
              }
            : null,
        mobile_phone: get('mPhoneNumber'),
        home_phone: get('hPhoneNumber'),
        other_phone: get('oPhoneNumber'),
        rep_nominate: haveRep,
        rep_first_name: haveRep ? get('repFirstName') : '',
        rep_last_name: haveRep ? get('repLastName') : '',
        rep_preferred_method_of_contact: haveRep ? get('repContactMethod') : null,
        rep_email_address: haveRep && get('repContactMethod') === 'email' ? get('repEmailAddress') : null,
        rep_postal_address:
          haveRep && get('repContactMethod') === 'post'
            ? {
                country: get('repPosCountry'),
                street_address: get('repPosStreetAddress'),
                suburb: get('repPosSuburb'),
                state: get('repPosState'),
                postcode: get('repPosPostcode'),
              }
            : null,
        rep_mobile_phone: haveRepWithPhone ? get('repMPhoneNumber') : null,
        rep_home_phone: haveRepWithPhone ? get('repHPhoneNumber') : null,
        rep_other_phone: haveRepWithPhone ? get('repOPhoneNumber') : null,
      },
    },

    tell_us_more: {
      hardship: {
        circumstances: get('reason'),
        nature_of_assistance: assistanceListValues[getAssistanceIndex],
        assistance_description: get('description'),
      },
    },
    employment: {
      current_employment: toBooleanString(get('employmentStatus') as YesNoString),
      employment: employmentData,
    },
    income: {
      other_income: toBooleanString(get('incomeStatus') as YesNoString),
      income: incomes,
      expenses: toExpenseArray(expenses),
      declaration: isDeclared,
    },
  };
};
